import {
  getActiveTimeMs,
  resetActiveTime,
  startActivityTracking,
  stopActivityTracking,
} from './activityTracker'

/** Callback function invoked when a session ends with metrics data */
type SessionEndCallback = (metrics: SessionEndMetrics) => void

interface KestrelSessionContext {
  vmId: string | null
  kestrelModelId: string
  userEmail: string
  inspectionType?: string
  inspectableSqft?: number
  unitId?: string
}

/**
 * Session metrics data passed to the onSessionEnd callback.
 * Contains all the metrics collected during the session.
 */
export interface SessionEndMetrics {
  userEmail: string
  inspectableSqft: number | null
  sessionDurationMs: number
  activeTimeMs: number
  annotationsCreated: number
  measurementsCreated: number
  reportsGenerated: number
  // Data validation metrics
  dataValidationTotalTimeMs: number
  dataValidationTimeToExhaustMs: number | null
  dataValidationAnnotationCount: number | null
  // Report extension metrics
  reportExtensionTotalTimeMs: number
  reportTextToGenerateMs: number | null
}

interface ActiveSession {
  startTime: number
  context: KestrelSessionContext
  annotationsCreated: number
  measurementsCreated: number
  reportsGenerated: number
  reportGeneratedTime: number | null
  dataValidationTimeMs: number
  dataValidationStartTime: number | null
  dataValidationListExhaustedTime: number | null
  dataValidationAnnotationCount: number | null
  reportExtensionTimeMs: number
  reportExtensionStartTime: number | null
  reportFirstTextEntryTime: number | null
}

let activeSession: ActiveSession | null = null
let sessionEndCallback: SessionEndCallback | null = null

const SQ_CM_TO_SQ_FT = 929.0304

/**
 * Beforeunload handler that ensures session metrics are saved when the user closes the tab.
 */
function handleBeforeUnload(): void {
  if (activeSession) {
    endKestrelSession()
  }
}

/**
 * Registers the beforeunload event listener.
 */
function registerBeforeUnloadHandler(): void {
  window.addEventListener('beforeunload', handleBeforeUnload)
}

/**
 * Removes the beforeunload event listener.
 */
function unregisterBeforeUnloadHandler(): void {
  window.removeEventListener('beforeunload', handleBeforeUnload)
}

/**
 * Calculates total inspectable square footage from inspection areas.
 * Inspection area values are in square centimeters, converted to square feet.
 */
export function calculateInspectableSqft(
  inspectionAreas: ReadonlyArray<{ area?: number }> | undefined
): number | null {
  if (!inspectionAreas || inspectionAreas.length === 0) return null

  const totalSqCm = inspectionAreas.reduce((sum, ia) => sum + (ia.area ?? 0), 0)
  if (totalSqCm === 0) return null

  return totalSqCm / SQ_CM_TO_SQ_FT
}

/**
 * Starts tracking a Kestrel VM session.
 * The session will be ended when endKestrelSession is called.
 * @param context - Session context with model ID, user email, etc.
 * @param onSessionEnd - Optional callback invoked when the session ends with metrics data
 */
export function startKestrelSession(
  context: KestrelSessionContext,
  onSessionEnd?: SessionEndCallback
): void {
  if (activeSession) {
    endKestrelSession()
  }

  // Store the callback for use when session ends
  sessionEndCallback = onSessionEnd ?? null

  // Register beforeunload handler to save metrics on tab close
  registerBeforeUnloadHandler()

  // Start activity tracking immediately so we capture all user interactions
  startActivityTracking()

  // Reset active time for the new session (now that tracking is started)
  resetActiveTime()

  activeSession = {
    startTime: Date.now(),
    context,
    annotationsCreated: 0,
    measurementsCreated: 0,
    reportsGenerated: 0,
    reportGeneratedTime: null,
    dataValidationTimeMs: 0,
    dataValidationStartTime: null,
    dataValidationListExhaustedTime: null,
    dataValidationAnnotationCount: null,
    reportExtensionTimeMs: 0,
    reportExtensionStartTime: null,
    reportFirstTextEntryTime: null,
  }
}

/**
 * Records when an annotation is created during the session.
 */
export function recordAnnotationCreated(): void {
  if (!activeSession) return
  activeSession.annotationsCreated++
}

/**
 * Records when a measurement is created during the session.
 */
export function recordMeasurementCreated(): void {
  if (!activeSession) return
  activeSession.measurementsCreated++
}

/**
 * Records when a report is generated during the session.
 */
export function recordReportGenerated(): void {
  if (!activeSession) return

  activeSession.reportsGenerated++
  activeSession.reportGeneratedTime = Date.now()
}

/**
 * Records when the user opens the data validation panel.
 * Call this when the annotation analysis modal is opened.
 */
export function recordDataValidationStart(): void {
  if (!activeSession) return

  // If already in data validation, don't restart the timer
  if (activeSession.dataValidationStartTime !== null) return

  activeSession.dataValidationStartTime = Date.now()
}

/**
 * Records when the user closes the data validation panel.
 * Call this when the annotation analysis modal is closed.
 */
export function recordDataValidationEnd(): void {
  if (!activeSession) return

  // Only record if we have a start time
  if (activeSession.dataValidationStartTime === null) return

  const validationDuration = Date.now() - activeSession.dataValidationStartTime
  activeSession.dataValidationTimeMs += validationDuration
  activeSession.dataValidationStartTime = null
}

/**
 * Records when the user has exhausted the annotation list for the first time.
 * This is the end point for measuring annotation analysis time.
 * Call this when currentIndex reaches the last annotation in the list.
 */
export function recordDataValidationListExhausted(
  annotationCount: number
): void {
  if (!activeSession) return

  // Only record the first time the list is exhausted
  if (activeSession.dataValidationListExhaustedTime !== null) return

  activeSession.dataValidationListExhaustedTime = Date.now()
  activeSession.dataValidationAnnotationCount = annotationCount
}

/**
 * Records when the user first enters text into the report form.
 * This is the start point for measuring report generation time.
 * Call this on the first onChange event in any report form field.
 */
export function recordReportFirstTextEntry(): void {
  if (!activeSession) return

  // Only record the first text entry
  if (activeSession.reportFirstTextEntryTime !== null) return

  activeSession.reportFirstTextEntryTime = Date.now()
}

/**
 * Records when the user opens the report generation extension.
 * Call this when the reporting sidebar is mounted.
 */
export function recordReportExtensionStart(): void {
  if (!activeSession) return

  // If already in report extension, don't restart the timer
  if (activeSession.reportExtensionStartTime !== null) return

  activeSession.reportExtensionStartTime = Date.now()
}

/**
 * Records when the user closes the report generation extension.
 * Call this when the reporting sidebar is unmounted.
 */
export function recordReportExtensionEnd(): void {
  if (!activeSession) return

  // Only record if we have a start time
  if (activeSession.reportExtensionStartTime === null) return

  const extensionDuration = Date.now() - activeSession.reportExtensionStartTime
  activeSession.reportExtensionTimeMs += extensionDuration
  activeSession.reportExtensionStartTime = null
}

/**
 * Ends the current Kestrel session and records all metrics.
 * Called when the user disconnects, navigates away, or closes the viewer.
 */
export function endKestrelSession(): void {
  if (!activeSession) return

  // Get final active time BEFORE stopping tracking (stopActivityTracking sets state to null)
  const activeTimeMs = getActiveTimeMs()
  stopActivityTracking()
  const sessionDurationMs = Date.now() - activeSession.startTime

  // Finalize data validation time if still open
  if (activeSession.dataValidationStartTime !== null) {
    activeSession.dataValidationTimeMs +=
      Date.now() - activeSession.dataValidationStartTime
  }

  // Finalize report extension time if still open
  if (activeSession.reportExtensionStartTime !== null) {
    activeSession.reportExtensionTimeMs +=
      Date.now() - activeSession.reportExtensionStartTime
  }

  // Time from data validation start to list exhaustion (null if list never exhausted)
  const dataValidationTimeToExhaustMs =
    activeSession.dataValidationStartTime !== null &&
    activeSession.dataValidationListExhaustedTime !== null
      ? activeSession.dataValidationListExhaustedTime -
        activeSession.dataValidationStartTime
      : null

  // Time from first text entry to report generation (null if report never generated)
  const reportTextToGenerateTimeMs =
    activeSession.reportFirstTextEntryTime !== null &&
    activeSession.reportGeneratedTime !== null
      ? activeSession.reportGeneratedTime -
        activeSession.reportFirstTextEntryTime
      : null

  // Call the session end callback with metrics for Rolodex persistence
  if (sessionEndCallback) {
    const metrics: SessionEndMetrics = {
      userEmail: activeSession.context.userEmail,
      inspectableSqft: activeSession.context.inspectableSqft ?? null,
      sessionDurationMs,
      activeTimeMs,
      annotationsCreated: activeSession.annotationsCreated,
      measurementsCreated: activeSession.measurementsCreated,
      reportsGenerated: activeSession.reportsGenerated,
      dataValidationTotalTimeMs: activeSession.dataValidationTimeMs,
      dataValidationTimeToExhaustMs,
      dataValidationAnnotationCount:
        activeSession.dataValidationAnnotationCount,
      reportExtensionTotalTimeMs: activeSession.reportExtensionTimeMs,
      reportTextToGenerateMs: reportTextToGenerateTimeMs,
    }
    sessionEndCallback(metrics)
  }

  // Cleanup
  unregisterBeforeUnloadHandler()
  sessionEndCallback = null
  activeSession = null
}

/**
 * Checks if there's an active Kestrel session.
 */
export function hasActiveSession(): boolean {
  return activeSession !== null
}
